'use client'

import { useState } from 'react'
import type { InvestableAssetsRange } from '@nwwp/database'
import { Input } from '@nwwp/ui-shadcn'
import { NumberInput } from '../controls/NumberInput'
import { RangeSelectInput } from '../controls/RangeSelectInput'
import { parseCurrency } from '../utils/currency-format'
import { INVESTABLE_ASSETS_OPTIONS } from '../utils/lead-magnet-qualification'

const FIELD_LABEL = 'text-subtitle font-bold text-neutral-darker uppercase'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** Contact + scoring fields every gated tool's unlock gate submits. */
export type LeadGateContact = {
  first_name: string
  last_name: string
  email: string
  /** US phone in E.164 format: +1 followed by 10 digits. */
  phone: string
  annual_income: number
  investable_assets_range: InvestableAssetsRange
}

type Values = { firstName: string; lastName: string; income: string; assets: string; email: string; phone: string }

/** State + validation for `LeadGateFields`. `contact` is null until every field is valid. */
export function useLeadGateFields() {
  const [values, setValues] = useState<Values>({
    firstName: '',
    lastName: '',
    income: '',
    assets: '',
    email: '',
    phone: '',
  })
  const [showErrors, setShowErrors] = useState(false)

  const income = parseCurrency(values.income)
  const phoneDigits = values.phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
  const invalid = {
    firstName: values.firstName.trim().length === 0,
    lastName: values.lastName.trim().length === 0,
    income: income <= 0,
    assets: values.assets === '',
    email: !EMAIL_PATTERN.test(values.email.trim()),
    phone: !/^[2-9]\d{9}$/.test(phoneDigits),
  }
  const valid = !Object.values(invalid).some(Boolean)
  const contact: LeadGateContact | null = valid
    ? {
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        email: values.email.trim(),
        phone: `+1${phoneDigits}`,
        annual_income: income,
        investable_assets_range: values.assets as InvestableAssetsRange,
      }
    : null

  const set = (key: keyof Values) => (value: string) => setValues((prev) => ({ ...prev, [key]: value }))

  return { values, set, invalid, contact, showErrors, revealErrors: () => setShowErrors(true) }
}

type Props = {
  gate: ReturnType<typeof useLeadGateFields>
  /** Prefix for input ids, unique per page. */
  idPrefix: string
  assetsOptions?: { value: string; label: string }[]
  assetsPlaceholder?: string
  assetsErrorMessage?: string
  incomePlaceholder?: string
}

/** Labeled name / income / assets / email / phone fields shared by gated-tool unlock gates. */
export function LeadGateFields({
  gate: { values, set, invalid, showErrors },
  idPrefix,
  assetsOptions = INVESTABLE_ASSETS_OPTIONS,
  assetsPlaceholder,
  assetsErrorMessage = 'Please select your investable assets.',
  incomePlaceholder,
}: Props) {
  const err = (condition: boolean, message: string) => (showErrors && condition ? message : undefined)
  const field = (id: string, label: string, input: React.ReactNode, className = 'flex flex-col gap-2') => (
    <div className={className}>
      <label htmlFor={`${idPrefix}-${id}`} className={FIELD_LABEL}>
        {label}
      </label>
      {input}
    </div>
  )

  return (
    <>
      <div className="flex flex-col gap-3 tablet:flex-row">
        {field(
          'first-name',
          'First Name',
          <Input
            id={`${idPrefix}-first-name`}
            name="first_name"
            type="text"
            autoComplete="given-name"
            value={values.firstName}
            onChange={(e) => set('firstName')(e.target.value)}
            error={err(invalid.firstName, 'Please enter your first name.')}
          />,
          'flex flex-1 flex-col gap-2'
        )}
        {field(
          'last-name',
          'Last Name',
          <Input
            id={`${idPrefix}-last-name`}
            name="last_name"
            type="text"
            autoComplete="family-name"
            value={values.lastName}
            onChange={(e) => set('lastName')(e.target.value)}
            error={err(invalid.lastName, 'Please enter your last name.')}
          />,
          'flex flex-1 flex-col gap-2'
        )}
      </div>

      {field(
        'income',
        'Household Income',
        <NumberInput
          id={`${idPrefix}-income`}
          name="annual_income"
          prefix="$"
          decimalScale={0}
          allowNegative={false}
          placeholder={incomePlaceholder}
          value={values.income}
          onChange={(e) => set('income')(e.target.value)}
          error={err(invalid.income, 'Please enter your household income.')}
        />
      )}

      <RangeSelectInput
        id={`${idPrefix}-assets`}
        name="investable_assets_range"
        label="Investable Assets"
        labelClassName={FIELD_LABEL}
        placeholder={assetsPlaceholder}
        value={values.assets}
        options={assetsOptions}
        onChange={set('assets')}
        error={err(invalid.assets, assetsErrorMessage)}
      />

      {field(
        'email',
        'Email',
        <Input
          id={`${idPrefix}-email`}
          name="email"
          type="email"
          autoComplete="email"
          placeholder="you@email.com"
          value={values.email}
          onChange={(e) => set('email')(e.target.value)}
          error={err(
            invalid.email,
            values.email.length === 0 ? 'Please enter your email address.' : 'Please enter a valid email address.'
          )}
        />
      )}

      {field(
        'phone',
        'Phone Number',
        <NumberInput
          id={`${idPrefix}-phone`}
          name="phone"
          mask="phone"
          placeholder="(555) 555-5555"
          autoComplete="tel"
          value={values.phone}
          onChange={(e) => set('phone')(e.target.value)}
          error={err(
            invalid.phone,
            values.phone.length === 0 ? 'Please enter your phone number.' : 'Please enter a valid phone number.'
          )}
        />
      )}
    </>
  )
}
